// janus-engine/reactions/php-row-rules.ts — lane RX (2026-09-28): the PHP row readers the TriggerReaders adapters share, each a
// twin of ONE PHP helper (folded from the RX adapter review — Codex, 2026-09-28). Pure; an input PHP could read but the page cannot
// (a swarm's occupied width, a declared charge pool with no held count) throws TriggerInputUnavailable.
import { TriggerInputUnavailable, heldLayerlessDefinition, type Row, type TriggerReaders } from './trigger-dispatch.js';
import { ABILITY_CHARGE_BUCKETS, abilityEntryId } from '../ability/charges/charge-entry.js';
import { entryCharges } from '../actions/ability-preconditions.js';
import { calcPl } from '../pl/calc-pl.js';
import { createPlReadContext } from '../pl/read-context.js';
import { readItemField } from '../compile/item-field-reader.js';
import { plTotal } from '../pl/xref.js';
export { plTotal } from '../pl/xref.js';

const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
export const FRIEND_TEAM = '7DBEB6D4-D147-703A-022E-05B290BB95C3';
export const ENEMY_TEAM = '9399856B-2132-7574-1684-91139A2831DD';

/** A PHP list JSON may carry as an OBJECT (a keyed array with gaps). */
export function phpList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const rec = record(value);
  return rec ? Object.values(rec) : [];
}
/** encounter_creature_field(): [0]-wrapped legacy values unwrap, an {items:{item_id}} shell resolves to its id. */
function ecField(row: Row | null, key: string): unknown {
  let v: unknown = record(row?.encounter_creatures)?.[key];
  while (Array.isArray(v)) v = v[0];
  const shell = record(v);
  if (shell && record(shell.items)) v = record(shell.items)!.item_id;
  return v;
}
/** encounter_field(): the `encounters` bucket first, then the flat key. */
function encounterField(encounter: Row, key: string): unknown {
  const bucket = record(encounter.encounters);
  return bucket && key in bucket ? bucket[key] : encounter[key];
}
/** ft_normalize_combat_id(). */
function combatId(value: unknown): string | null {
  let v = value; while (Array.isArray(v)) v = v[0];
  const id = String(v ?? '').trim().toUpperCase();
  return id !== '' && UUID.test(id) ? id : null;
}
/** ft_creature_matches_active_combat(): the tagged membership, else a WHOLLY untagged legacy combat's RESIDENT with no combat id. */
export function inActiveCombat(encounter: Row, row: Row): boolean {
  const active = combatId(encounterField(encounter, 'active_combat_id'));
  const member = combatId(ecField(row, 'combat_id'));
  if (active !== null && member !== null && active === member) return true;
  const roster = encounter.encounter_creatures_arr;
  if (Math.trunc(Number(encounterField(encounter, 'in_combat') ?? 0)) !== 1 || active !== null || !Array.isArray(roster) || roster.length === 0) return false;
  for (const resident of roster) if (record(resident) && combatId(ecField(record(resident), 'combat_id')) !== null) return false;
  if (member !== null) return false;
  const id = String(ecField(row, 'encounter_creature_id') ?? '').toUpperCase();
  if (id === '') return false;
  return roster.some(resident => String(ecField(record(resident), 'encounter_creature_id') ?? '').toUpperCase() === id);
}
/** encounter_row_team_uuid() (as stored — PHP compares it strictly with the upper-case TeamType ids). */
export function teamUuid(row: Row, layerValue: (row: Row, key: string) => unknown): string | null {
  let t = ecField(row, 'team');
  if (t === undefined || t === null) { t = layerValue(row, 'team'); while (Array.isArray(t)) t = t[0]; }
  if (t === null || t === undefined || t === '' || typeof t === 'object') return null;
  return String(t);
}
/** check_opposing_team(). */
export function opposingTeams(a: Row, b: Row, layerValue: (row: Row, key: string) => unknown): boolean {
  const ta = teamUuid(a, layerValue), tb = teamUuid(b, layerValue);
  const valid = (t: string | null): boolean => t === FRIEND_TEAM || t === ENEMY_TEAM;
  return valid(ta) && valid(tb) && ta !== tb;
}
/** A definition's PL the front-end way: the held number (the served stash / a heal applied locally), else priced now by the browser PL
 *  engine (calc_pl twin) over the definitions the page holds; null when the page cannot price it (a closure definition not held). */
export function frontEndPl(definition: Row, lookup: (id: string) => Row | null, catalog: Row | null): number | null {
  const held = plTotal(definition);
  if (held !== null) return held;
  const id = String(readItemField(definition, 'item_id') ?? '').toUpperCase();
  // Inline carrier/status definitions retain layers while omitting their computed price. The page's
  // definition store may already hold the complete definition; use its PL before asking for a closure.
  const resolved = id ? lookup(id) : null;
  const resolvedPl = resolved ? plTotal(resolved) : null;
  if (resolvedPl !== null) return resolvedPl;
  const source = resolved ?? definition;
  if (PRICED.has(source)) return PRICED.get(source)!;
  const ctx = createPlReadContext({ catalog, lookup: key => { const d = lookup(key); return d ? { def: d } : { missing: true }; } });
  const out = calcPl(definition, ctx);
  if (!out.ok) return null;
  PRICED.set(source, out.result.pl);
  return out.result.pl;
}
/** Definition identity scopes prices: a save/replacement cannot inherit an older definition's number. */
const PRICED = new WeakMap<Row, number>();

/** Warm cold reaction prices before the synchronous dispatch. The page's existing PL bridge resolves
 * and prices its dependency closure; no server rule or PL-claim write is needed. */
export async function hydrateTriggerPl(definitions: readonly Row[], lookup: (id: string) => Row | null, catalog: Row | null,
  price: (ids: string[], onRow: (row: { id: string; pl: number | null }) => void) => Promise<unknown>): Promise<void> {
  const missing = new Map<string, Row>();
  for (const def of definitions) {
    const id = String(readItemField(def, 'item_id') ?? '').toUpperCase();
    if (UUID.test(id) && frontEndPl(def, lookup, catalog) === null) missing.set(id, lookup(id) ?? def);
  }
  if (!missing.size) return;
  await price([...missing.keys()], row => {
    const source = missing.get(row.id.toUpperCase());
    if (source && typeof row.pl === 'number' && Number.isFinite(row.pl)) {
      PRICED.set(source, row.pl);
      const current = lookup(row.id.toUpperCase());
      if (current) PRICED.set(current, row.pl);
    }
  });
}

/** pos_coord(): the `position` / `pos_arr` container (or a bare one), [0]-unwrapped, numeric → number, else null. */
export function posCoord(row: Row, axis: 'x_pos' | 'y_pos' | 'z_pos'): number | null {
  const container = record(row.position) ?? record(row.pos_arr) ?? (('x_pos' in row || 'y_pos' in row || 'z_pos' in row) ? row : null);
  if (!container || !(axis in container)) return null;
  let v = container[axis]; if (Array.isArray(v)) v = v[0];
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  return typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null;
}
/** creature_statuses(row, 'target') catalog ids — the stored rows (a keyed/gapped container included). */
export function storedStatusItemIds(row: Row): string[] {
  const out: string[] = [];
  for (const raw of phpList(record(row.statuses)?.target)) {
    const s = record(raw); if (!s) continue;
    let id: unknown = s.status_item_id ?? record(record(s.status_arr)?.items)?.item_id;
    while (Array.isArray(id)) id = id[0];
    if (typeof id === 'string' && id !== '') out.push(id.toUpperCase());
  }
  return out;
}
/** creature_natural_weapon_entries(row).length: the authored list AS IS, plus derived entries not already in it. */
export function naturalWeaponCount(row: Row, entries: (row: Row, key: string) => unknown[]): number {
  const uid = (e: unknown): string => typeof e === 'string' ? e : String(record(e)?.ability_id ?? record(record(e)?.items)?.item_id ?? record(e)?.item_id ?? '');
  const authored = entries(row, 'natural_weapon');
  const seen = new Set(authored.map(uid).filter(Boolean));
  let n = authored.length;
  const derived = record(record(row.computed)?.creature)?.natural_weapon;
  for (const d of Array.isArray(derived) ? derived : []) {
    const id = uid(d);
    if (id !== '' && seen.has(id)) continue;
    if (id !== '') seen.add(id);
    n++;
  }
  return n;
}
/** The held count of a DECLARED charge pool (ability_entry_current_charges); a declared pool with no held count throws. */
export function heldCharges(entry: Row, layerValue: (row: Row | null, key: string) => unknown): unknown {
  const charges = record(entry.charges);
  if (charges && 'current' in charges) return charges.current;
  if ('ability_charges' in entry) return entry.ability_charges;
  const def = record(entry.item_arr);
  if (def && layerValue(def, 'ability_charges') != null) throw new TriggerInputUnavailable('charges-not-held');
  return 'NA';
}
/** A trigger snapshot may predate charge hydration. Only a held pool on this caster can fill its missing count. */
export function heldTriggerCharges(entry: Row, abilityId: string, caster: Row,
  read: Pick<TriggerReaders, 'entries' | 'value' | 'definition' | 'roster'>): unknown {
  try { return heldCharges(entry, read.value); }
  catch (error) {
    if (!(error instanceof TriggerInputUnavailable) || error.reason !== 'charges-not-held') throw error;
    const holderId = String(ecField(caster, 'encounter_creature_id') ?? '').toUpperCase();
    const held = holderId ? read.roster?.().find(row => String(ecField(row, 'encounter_creature_id') ?? '').toUpperCase() === holderId) : null;
    const holders = held && held !== caster ? [caster, held] : [caster];
    for (const holder of holders) for (const bucket of ABILITY_CHARGE_BUCKETS) for (const raw of read.entries(holder, bucket)) {
      const candidate = record(raw);
      if (!candidate || abilityEntryId(candidate) !== abilityId.toUpperCase()) continue;
      const pool = entryCharges(candidate, abilityId, { value: read.value, resolveItem: id => read.definition(String(id ?? '').toUpperCase()) });
      if (pool?.currentHeld) return pool.current;
    }
    throw error;
  }
}
const RANGE_SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const RANGE_UNLIMITED = 'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED';
/**
 * janus_trigger_entry_in_range()'s is_target_in_range() leg for an authored range. PHP reads it through layer_has(), which is true
 * whenever the result carries an `in_range` KEY — so PHP's gate never refuses (180/180 oracle answers true); an out-of-range
 * dispatch is silently vetoed a step later by validate_and_adjust_targets. The front end states the real rule instead: a Self
 * reaction (retargeted to its owner) and an Unlimited one are always in range; any other authored range needs the range port
 * (is_target_in_range) the browser does not hold yet → unavailable (hand back).
 */
export function triggerRangePasses(ability: Row, layerValue: (row: Row | null, key: string) => unknown): boolean {
  let range = layerValue(ability, 'range');
  while (Array.isArray(range)) range = range[0];
  const id = String(range ?? '').trim().toUpperCase();
  if (id === RANGE_SELF || id === RANGE_UNLIMITED) return true;
  throw new TriggerInputUnavailable('is-target-in-range-not-ported');
}
/** The in-PIPELINE twin of the same gate (lane RX #5, 2026-09-29): PHP's gate is inert, and the browser executor now measures the
 *  authored range itself at dispatch (ability/range-check.ts — validate_and_adjust_targets' silent skip), so a range that check can
 *  measure passes here; any other still hands back. The page-row readers keep triggerRangePasses (their callers run no range veto). */
const MEASURED_RANGES = new Set(['36D59FF4-6778-96ED-8664-F5D63638C77B', '4C761395-2532-3F23-7CBA-CA81AF5C0855', 'A95F219A-6288-6B17-6DB7-03DE4B97AE96',
  'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3', 'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA']);
export function triggerRangeGateInert(ability: Row, layerValue: (row: Row | null, key: string) => unknown): boolean {
  let range = layerValue(ability, 'range');
  while (Array.isArray(range)) range = range[0];
  const id = String(range ?? '').trim().toUpperCase();
  if (MEASURED_RANGES.has(id) || /^\d+$/.test(id)) return true;
  return triggerRangePasses(ability, layerValue);
}
/** A catalog definition the page holds IN FULL: layers, or its own markers / complete lean catalog row — never a bare
 *  identity shell {items:{item_id, item_name, item_type}} (heldLayerlessDefinition). */
export function fullDefinition(def: Row | null, allowLayerlessStatus: boolean | 'any' | undefined): Row | null {
  if (!def || !record(def.items)) return null;
  const layers = record(def.item_layer_types);
  if (layers && Object.keys(layers).length) return def;
  // 'any': a layer-less CARRIER of any type (a trigger carrier with nothing to fire — lane RX #5, 2026-09-29)
  if (!allowLayerlessStatus || (allowLayerlessStatus !== 'any' && record(def.items)!.item_type !== 'status_effect')) return null;
  return heldLayerlessDefinition(def, allowLayerlessStatus === 'any') ? def : null;
}
